const ASearchBuildingSelector = '.a-search-building';

class ASearchBuilding {
  constructor(element) {
    this.component = element;
    if (!this.component) return;

    this.addressForm = this.component.querySelector('#address-form');
    this.zipcodeInput = this.component.querySelector('#zipcode');
    this.housenumberInput = this.component.querySelector('#housenumber');
    this.additionInput = this.component.querySelector('#addition');
    this.inputs = [
      this.zipcodeInput,
      this.housenumberInput,
      this.additionInput,
    ];

    this.noAddressFound = this.component.querySelector('#no-address-found');
    this.apiError = this.component.querySelector('#api-error');
    this.spinner = this.component.querySelector('#spinner');

    this.datalist = this.component.querySelector('#building-results');
    this.buttonClear = this.component.querySelector('#button-clear');
    this.errorMessages = {
      zipcodeRequired: this.component.querySelector('#error-zipcode-required'),
      zipcodeInvalid: this.component.querySelector('#error-zipcode-invalid'),
      housenumberRequired: this.component.querySelector(
        '#error-housenumber-required'
      ),
      housenumberInvalid: this.component.querySelector(
        '#error-housenumber-invalid'
      ),
    };
    this.isFormSubmitted = false;
  }

  init() {
    if (!this.component) return;
    this.hideErrorMessages();

    if (this.inputs.length > 0) {
      this.inputs.forEach((input) =>
        input.addEventListener('input', () => this.toggleClearButton())
      );
    }

    if (this.datalist) {
      this.datalist.addEventListener('DOMSubtreeModified', () =>
        this.toggleClearButton()
      );
    }

    if (this.buttonClear) {
      this.buttonClear.addEventListener('click', (event) => {
        event.preventDefault();
        this.clearForm();
      });
    }

    if (this.addressForm) {
      this.addressForm.addEventListener('submit', (event) =>
        this.handleSubmit(event)
      );
    }

    if (this.zipcodeInput) {
      this.zipcodeInput.addEventListener('input', () => this.validateOnInput());
    }
    if (this.housenumberInput) {
      this.housenumberInput.addEventListener('input', () =>
        this.validateOnInput()
      );
    }

    this.component.addEventListener('click', (event) => {
      const button = event.target.closest('.copy-button');
      if (!button) return;

      const key = button.getAttribute('data-key');
      const copyText = button.getAttribute('data-copy');
      this.copyValue(key, copyText);
    });
  }

  copyValue(elementId, copyText) {
    navigator.clipboard
      .writeText(copyText)
      .then(() => {
        var checkIcon = this.component.querySelector(
          `#check-icon-${elementId}`
        );

        if (checkIcon) {
          checkIcon.classList.add('visible');

          setTimeout(() => {
            checkIcon.classList.remove('visible');
          }, 1000);
        }
      })
      .catch(() => {
        console.error('Er is iets misgegaan met het kopiëren!');
      });
  }
  toggleClearButton() {
    const hasInput = Array.from(this.inputs).some(
      (input) => input.value.trim() !== ''
    );
    const hasResults = this.datalist.children.length > 0;
    this.buttonClear.hidden = !(hasInput || hasResults);
  }

  clearForm() {
    this.inputs.forEach((input) => (input.value = ''));
    this.datalist.innerHTML = '';
    this.noAddressFound.hidden = true;
    this.apiError.hidden = true;
    this.hideErrorMessages();
    this.toggleClearButton();

    this.isFormSubmitted = false;
  }

  hideErrorMessages() {
    Object.values(this.errorMessages).forEach((error) => (error.hidden = true));
  }

  showErrorMessage(errorType) {
    if (this.errorMessages[errorType]) {
      this.errorMessages[errorType].hidden = false;
    }
  }

  validateOnInput() {
    if (!this.isFormSubmitted) return;

    this.hideErrorMessages();

    const zipcodeError = this.validateZipcode();
    const housenumberError = this.validateHousenumber();

    if (zipcodeError) this.showErrorMessage(zipcodeError);
    if (housenumberError) this.showErrorMessage(housenumberError);
  }

  validateField(value, regex, requiredError, invalidError) {
    if (!value) return requiredError;
    if (regex && !regex.test(value)) return invalidError;
    return null;
  }

  validateZipcode() {
    return this.validateField(
      this.zipcodeInput.value.trim(),
      /^[1-9][0-9]{3}[ ]?[A-Za-z]{2}$/,
      'zipcodeRequired',
      'zipcodeInvalid'
    );
  }

  validateHousenumber() {
    return this.validateField(
      this.housenumberInput.value.trim(),
      /^[0-9]+$/,
      'housenumberRequired',
      'housenumberInvalid'
    );
  }

  handleSubmit(event) {
    this.isFormSubmitted = true;
    this.hideErrorMessages();
    event.preventDefault();

    this.datalist.innerHTML = '';

    const zipcodeError = this.validateZipcode();
    const housenumberError = this.validateHousenumber();

    if (zipcodeError) this.showErrorMessage(zipcodeError);
    if (housenumberError) this.showErrorMessage(housenumberError);

    if (!zipcodeError && !housenumberError) {
      this.fetchBuildingData();
    }
  }

  fetchBuildingData() {
    this.spinner.hidden = false;
    this.noAddressFound.hidden = true;
    this.apiError.hidden = true;

    const requestPayload = {
      ZoekenAdressen: {
        Postcode: this.zipcodeInput.value.replace(/\s+/g, '').toUpperCase(),
        Huisnummer: this.housenumberInput.value,
        HuisnummerToevoeging: this.additionInput.value,
      },
    };

    fetch('/apiproxy/achmea-schade-gebouwen-v1-ophalengebouwkenmerken', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        __RequestVerificationToken: this.component.querySelector(
          'input[name="__RequestVerificationToken"]'
        )?.value,
      },
      body: JSON.stringify(requestPayload),
    })
      .then((response) => response.json())
      .then((data) => this.handleApiResponse(data))
      .catch((error) => this.handleApiError(error));
  }

  handleApiResponse(data) {
    this.spinner.hidden = true;
    this.datalist.innerHTML = '';

    if (!data.gebouwKenmerken) {
      this.noAddressFound.hidden = false;
      return;
    }

    const results = this.formatBuildingData(data.gebouwKenmerken);

    Object.entries(results).forEach(([key, item]) => {
      const listItem = document.createElement('li');
      listItem.className = 'a-data-list-item';
      listItem.innerHTML = `
                            <div class="a-typeset">
                                <div class="a-stack a-stack--row a-stack--justify-between">
                                    <strong style="text-wrap: nowrap;">${key}</strong>
                                    <span id="value-${key.replace(/\s+/g, '-')}" style="text-align: right;">${item.display}</span>
                                </div>
                            </div>
                            <div class="a-data-list-item__action">
                              <button class="a-icon-button copy-button" title="Kopieer ${key}" name="document" type="button"
                                      data-key="${key}" data-copy="${item.copy}">
                                    <div class="a-state-ring"></div>
                                    <a-icon>
                                        <svg aria-hidden="true" focusable="false" class="a-icon a-icon--m a-icon-document">
                                            <use vector-effect="non-scaling-stroke" href="/assets/images/sprites/icons.svg#document"></use>
                                        </svg>
                                    </a-icon>
                                </button>
                            </div>
                            <a-icon name="check" id="check-icon-${key}" class="check-icon">
                                <svg aria-hidden="true" focusable="false" class="a-icon a-icon-check">
                                    <use vector-effect="non-scaling-stroke" href="/assets/images/sprites/icons.svg#check"></use>
                                </svg>
                            </a-icon>
                        `;
      this.datalist.appendChild(listItem);
    });
  }

  handleApiError(error) {
    console.error('API call failed:', error);
    this.spinner.hidden = true;
    this.noAddressFound.hidden = true;
    this.apiError.hidden = false;
  }

  formatBuildingData(gebouwKenmerken) {
    const numberFormatter = new Intl.NumberFormat('nl-NL');

    const propertiesList = JSON.parse(
      this.component.getAttribute('data-properties')
    );

    const buildingDataList = propertiesList.map((item) => ({
      apiProperty: item.ApiProperty,
      format: item.Format,
      numberFormatter: item.NumberFormatter,
      label: item.Label,
    }));

    return buildingDataList.reduce((acc, item) => {
      let value = gebouwKenmerken[item.apiProperty];

      if (item.numberFormatter) {
        value = numberFormatter.format(value);
      }

      acc[item.label] = {
        display: item.format ? item.format.replace('{value}', value) : value,
        copy: gebouwKenmerken[item.apiProperty],
      };

      return acc;
    }, {});
  }
}

// Initiate module
function ASearchBuildingCreateInstance(element) {
  if (!element.searchBuildingClass) {
    element.searchBuildingClass = new ASearchBuilding(element);
    element.searchBuildingClass.init();
  }
}

// Create new instances on DOM mutation
document
  .querySelectorAll(`${ASearchBuildingSelector}`)
  .forEach(ASearchBuildingCreateInstance);

const ASearchBuildingObserver = new MutationObserver(function (mutations) {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((element) => {
      document
        .querySelectorAll(`${ASearchBuildingSelector}`)
        .forEach(ASearchBuildingCreateInstance);
    });
  });
});

ASearchBuildingObserver.observe(document, {
  attributes: false,
  childList: true,
  characterData: false,
  subtree: true,
});
